import pageSettled from "../rendering/pageSettled";

// PostHog, once it has loaded; null until init() runs with a key, and when
// analytics is off. It is fetched once the page has settled, so it never
// competes with what the visitor came to see. Calls made before then run
// once it has, in the order they were made; events keep the time they
// happened.
let loaded = null;
let getCustomProps = () => ({});

// Runs `use` with PostHog once it has loaded; never if analytics is off or
// PostHog did not load.
const withPosthog = (use) => {
  if (loaded) loaded.then((posthog) => posthog && use(posthog));
};

export function init({ customProperties } = {}) {
  if (loaded) return;
  const key = process.env.REACT_APP_POSTHOG_KEY;
  if (!key) return;
  if (typeof customProperties === "function") {
    getCustomProps = customProperties;
  }
  loaded = pageSettled()
    .then(() => import("posthog-js"))
    .then(({ default: posthog }) => start(posthog, key))
    .catch((error) => {
      console.error("Analytics did not load, so this visit is not measured:", error);
      return null;
    });
}

function start(posthog, key) {
  posthog.init(key, {
    api_host: "https://avrah.it/ingest",
    ui_host: "https://eu.posthog.com",
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    // Persistent first-party analytics id (localStorage). The visitor id is
    // stored on the device, so the same browser is recognized across reloads
    // and return visits — that makes "unique users" a real headcount instead
    // of a throwaway id per page load (repeat opens of the same link collapse
    // to one visitor). This writes to the device, so unlike the previous
    // in-memory setup it falls under the ePrivacy Directive; we rely on the
    // first-party analytics exemption (Italy's Garante): first-party only, IP
    // dropped (see property_denylist), no session recording, no third-party
    // sharing, aggregate use only — treated like a technical cookie, disclosed
    // in the Cookie Policy rather than gated behind a consent banner.
    persistence: "localStorage",
    disable_session_recording: true,
    disable_surveys: true,
    person_profiles: "identified_only",
    property_denylist: ["$ip"],
  });
  // Attach our custom dimensions (locale, auth) as PostHog "super properties"
  // so they ride on EVERY event — including autocaptured $pageview / $pageleave —
  // not just manual track() calls. Without this, ~90% of events carried no locale,
  // forcing language analysis onto $browser_language (the browser setting, not what
  // we actually rendered).
  posthog.register(getCustomProps());
  return posthog;
}

// Update the displayed-language dimension after an i18n language change so it lands
// on all subsequent events (super property) and on the person profile (when identified).
export function setLocale(locale) {
  if (!loaded || !locale) return;
  const code = String(locale).slice(0, 2);
  withPosthog((posthog) => {
    posthog.register({ locale: code });
    posthog.setPersonProperties({ locale: code });
  });
}

export function track(name, options = {}) {
  if (!loaded) return;
  const props = (options && options.props) || {};
  const properties = { ...getCustomProps(), ...props };
  const timestamp = new Date();
  withPosthog((posthog) => posthog.capture(name, properties, { timestamp }));
}

// Properties to send with every event from now on, such as the campaign a
// visitor came from.
export function register(properties) {
  if (!loaded) return;
  withPosthog((posthog) => posthog.register(properties));
}

// Drop undefined/null entries so we never overwrite a real person property
// with a blank — and never send keys PostHog would store as empty.
const compact = (obj) =>
  Object.fromEntries(
    Object.entries(obj || {}).filter(([, v]) => v !== undefined && v !== null)
  );

// Two trait shapes are accepted, both backward compatible:
//   identify(id, { email, role })            -> flat $set traits
//   identify(id, { $set: {...}, $set_once: {...} })  -> explicit set/set-once
// `locale` is always folded into $set so the person profile carries the
// language we actually rendered (see setLocale).
export function identify(distinctId, traits = {}) {
  if (!loaded || !distinctId) return;
  const hasExplicitSet =
    traits &&
    (Object.prototype.hasOwnProperty.call(traits, "$set") ||
      Object.prototype.hasOwnProperty.call(traits, "$set_once"));
  // Login flips auth -> logged_in; refresh super properties so it shows on events.
  const superProperties = getCustomProps();

  if (hasExplicitSet) {
    const set = compact({ locale: superProperties.locale, ...traits.$set });
    const setOnce = compact(traits.$set_once);
    const payload = {};
    if (Object.keys(set).length) payload.$set = set;
    if (Object.keys(setOnce).length) payload.$set_once = setOnce;
    withPosthog((posthog) => {
      posthog.identify(distinctId, payload.$set, payload.$set_once);
      posthog.register(superProperties);
    });
  } else {
    const clean = compact({ locale: superProperties.locale, ...traits });
    withPosthog((posthog) => {
      posthog.identify(distinctId, clean);
      posthog.register(superProperties);
    });
  }
}

export function reset() {
  if (!loaded) return;
  // Re-establish super properties for the now-anonymous session (token already cleared).
  const superProperties = getCustomProps();
  withPosthog((posthog) => {
    posthog.reset(); // clears distinct id AND super properties
    posthog.register(superProperties);
  });
}

export function optOut() {
  if (!loaded) return;
  withPosthog((posthog) => posthog.opt_out_capturing());
}

export function optIn() {
  if (!loaded) return;
  withPosthog((posthog) => posthog.opt_in_capturing());
}

// Whether the visitor has opted out of analytics on this device, once
// PostHog has loaded to say; false when analytics is off.
export async function hasOptedOut() {
  const posthog = await loaded;
  return Boolean(posthog && posthog.has_opted_out_capturing());
}
